import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  LayoutDashboard,
  FileText,
  Image,
  Calendar,
  Mail,
  Users,
  Plus,
  Trash2,
  Edit,
  Upload,
  X,
  Building2,
  ExternalLink,
  Settings,
  Sparkles,
  TrendingUp,
  Shield,
  ShieldOff,
  UserPlus,
  UserMinus
} from "lucide-react";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900 text-white">
        <div className="container-custom py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-xs font-medium mb-3">
                <Settings className="w-3 h-3" />
                Administration
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Admin Dashboard</h1>
              <p className="text-primary-200 mt-1">Welcome back, {user?.name}</p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="view-site-btn">
                <Link to="/">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Site
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white border shadow-sm p-1 rounded-2xl flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-overview">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="committees" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-committees">
              <Building2 className="w-4 h-4 mr-2" />
              Committees
            </TabsTrigger>
            <TabsTrigger value="posts" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-posts">
              <FileText className="w-4 h-4 mr-2" />
              Posts
            </TabsTrigger>
            <TabsTrigger value="media" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-media">
              <Image className="w-4 h-4 mr-2" />
              Media
            </TabsTrigger>
            <TabsTrigger value="events" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-events">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-messages">
              <Mail className="w-4 h-4 mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-primary-900 data-[state=active]:text-white" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab stats={stats} />
          </TabsContent>

          <TabsContent value="committees">
            <CommitteesTab />
          </TabsContent>

          <TabsContent value="posts">
            <PostsTab />
          </TabsContent>

          <TabsContent value="media">
            <MediaTab />
          </TabsContent>

          <TabsContent value="events">
            <EventsTab />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesTab />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ stats }) => {
  const statCards = [
    { label: "Total Posts", value: stats?.posts || 0, icon: FileText, color: "from-blue-500 to-indigo-600", bgLight: "bg-blue-50" },
    { label: "Media Files", value: stats?.media || 0, icon: Image, color: "from-emerald-500 to-teal-600", bgLight: "bg-emerald-50" },
    { label: "Events", value: stats?.events || 0, icon: Calendar, color: "from-purple-500 to-violet-600", bgLight: "bg-purple-50" },
    { label: "Users", value: stats?.users || 0, icon: Users, color: "from-amber-500 to-orange-600", bgLight: "bg-amber-50" },
    { label: "Messages", value: stats?.messages || 0, icon: Mail, color: "from-pink-500 to-rose-600", bgLight: "bg-pink-50" },
  ];

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bento-card overflow-hidden" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <CardContent className="p-6">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4 shadow-lg`}>
                <stat.icon className="w-7 h-7 text-white" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bento-card p-6 cursor-pointer group" data-testid="quick-create-post">
            <Link to="/admin" onClick={() => document.querySelector('[data-testid="admin-tab-posts"]')?.click()} className="block">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-primary-900 transition-colors">Create Post</h3>
              <p className="text-sm text-gray-500 mt-1">Add news or announcements</p>
            </Link>
          </Card>
          
          <Card className="bento-card p-6 cursor-pointer group" data-testid="quick-upload-media">
            <Link to="/admin/media-gallery" className="block">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Upload className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-primary-900 transition-colors">Upload Media</h3>
              <p className="text-sm text-gray-500 mt-1">Add images, videos, audio</p>
            </Link>
          </Card>
          
          <Card className="bento-card p-6 cursor-pointer group" data-testid="quick-create-event">
            <Link to="/admin" onClick={() => document.querySelector('[data-testid="admin-tab-events"]')?.click()} className="block">
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-primary-900 transition-colors">Create Event</h3>
              <p className="text-sm text-gray-500 mt-1">Schedule upcoming events</p>
            </Link>
          </Card>
          
          <Card className="bento-card p-6 cursor-pointer group" data-testid="quick-view-messages">
            <Link to="/admin" onClick={() => document.querySelector('[data-testid="admin-tab-messages"]')?.click()} className="block">
              <div className="w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-primary-900 transition-colors">View Messages</h3>
              <p className="text-sm text-gray-500 mt-1">Check contact inquiries</p>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Committees Tab
const CommitteesTab = () => {
  const committees = [
    { id: "mosque-dawah", name: "Mosque and Da'wah", icon: "🕌", color: "from-emerald-500 to-teal-600", bgColor: "bg-emerald-50", hasMedia: true },
    { id: "project-maintenance", name: "Project and Maintenance", icon: "🔧", color: "from-blue-500 to-indigo-600", bgColor: "bg-blue-50", hasMedia: false },
    { id: "welfare-publicity", name: "Welfare and Publicity", icon: "💚", color: "from-pink-500 to-rose-600", bgColor: "bg-pink-50", hasMedia: false },
    { id: "finance-investment", name: "Finance and Investment", icon: "💰", color: "from-amber-500 to-orange-600", bgColor: "bg-amber-50", hasMedia: false },
    { id: "security-ethic", name: "Security and Ethics", icon: "🛡️", color: "from-purple-500 to-violet-600", bgColor: "bg-purple-50", hasMedia: false }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Manage Committees</h2>
        <p className="text-gray-600">Click on any committee to manage their posts, events, and media content.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {committees.map((committee) => (
          <Card key={committee.id} className="bento-card overflow-hidden group" data-testid={`committee-card-${committee.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-16 h-16 rounded-2xl ${committee.bgColor} flex items-center justify-center text-3xl group-hover:scale-110 transition-transform`}>
                  {committee.icon}
                </div>
                {committee.hasMedia && (
                  <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    Has Media Gallery
                  </span>
                )}
              </div>
              <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-900 transition-colors">
                {committee.name}
              </h3>
              <p className="text-sm text-gray-600 mb-6">Manage posts, events, and activities for this committee.</p>
              <Button asChild className="w-full bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid={`manage-committee-${committee.id}`}>
                <Link to={`/admin/committee/${committee.id}`}>
                  <Edit className="w-4 h-4 mr-2" />
                  Manage Activities
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Media Gallery Quick Link */}
      <Card className="p-6 rounded-3xl bg-gradient-to-br from-primary-50 to-emerald-50 border-primary-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-emerald-600 flex items-center justify-center shadow-lg">
              <Image className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-primary-900">Media Gallery Management</h3>
              <p className="text-sm text-primary-700">Upload and manage media files for the Mosque and Da'wah committee.</p>
            </div>
          </div>
          <Button asChild className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="quick-media-gallery-link">
            <Link to="/admin/media-gallery">
              <Image className="w-4 h-4 mr-2" />
              Manage Gallery
            </Link>
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Posts Tab
const PostsTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "news",
    committee: "",
    image_url: ""
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.post_id}`, formData, { withCredentials: true });
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/posts`, formData, { withCredentials: true });
        toast.success("Post created!");
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save post");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      toast.success("Post deleted!");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      category: post.category,
      committee: post.committee || "",
      image_url: post.image_url || ""
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData({ title: "", content: "", category: "news", committee: "", image_url: "" });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Manage Posts</h2>
          <p className="text-gray-600">Create and manage news, announcements, and activities.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="create-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Post
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 rounded-3xl shadow-lg border-0">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl font-semibold text-gray-900">
              {editingPost ? "Edit Post" : "Create New Post"}
            </h3>
            <Button variant="ghost" size="icon" onClick={resetForm} className="rounded-full">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Post title"
                  required
                  className="h-12 rounded-xl"
                  data-testid="post-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="post-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="activity">Activity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Committee (Optional)</Label>
                <Select value={formData.committee} onValueChange={(v) => setFormData({ ...formData, committee: v })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="post-committee-select">
                    <SelectValue placeholder="Select committee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mosque-dawah">Mosque and Da'wah</SelectItem>
                    <SelectItem value="project-maintenance">Project and Maintenance</SelectItem>
                    <SelectItem value="welfare-publicity">Welfare and Publicity</SelectItem>
                    <SelectItem value="finance-investment">Finance and Investment</SelectItem>
                    <SelectItem value="security-ethic">Security and Ethics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Image URL (Optional)</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  className="h-12 rounded-xl"
                  data-testid="post-image-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Content</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your post content..."
                rows={6}
                required
                className="rounded-xl resize-none"
                data-testid="post-content-input"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6" data-testid="post-submit-btn">
                {editingPost ? "Update Post" : "Create Post"}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl px-6">
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 rounded-2xl animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </Card>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <Card className="p-16 text-center rounded-3xl border-dashed">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Posts Yet</h3>
          <p className="text-gray-500">Create your first post to get started!</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.post_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`admin-post-${post.post_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      post.category === 'news' ? 'bg-blue-100 text-blue-700' :
                      post.category === 'announcement' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {post.category}
                    </span>
                    {post.committee && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{post.committee}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{post.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{post.content}</p>
                  <p className="text-xs text-gray-400 mt-3">
                    {format(new Date(post.created_at), "MMM d, yyyy")} by {post.author_name}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(post)} className="rounded-xl hover:bg-primary-50" data-testid={`edit-post-${post.post_id}`}>
                    <Edit className="w-4 h-4 text-primary-900" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(post.post_id)} className="rounded-xl hover:bg-red-50" data-testid={`delete-post-${post.post_id}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Media Tab
const MediaTab = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    media_type: "image",
    committee: "mosque-dawah"
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`);
      setMedia(response.data);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("media_type", formData.media_type);
      data.append("committee", formData.committee);
      data.append("file", file);

      await axios.post(`${API}/media`, data, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Media uploaded!");
      fetchMedia();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload media");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Are you sure you want to delete this media?")) return;
    try {
      await axios.delete(`${API}/media/${mediaId}`, { withCredentials: true });
      toast.success("Media deleted!");
      fetchMedia();
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setFormData({ title: "", description: "", media_type: "image", committee: "mosque-dawah" });
    setFile(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Manage Media</h2>
          <p className="text-gray-600">Upload and manage images, videos, audio, and documents.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="upload-media-btn">
          <Upload className="w-4 h-4 mr-2" />
          Upload Media
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 rounded-3xl shadow-lg border-0">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl font-semibold text-gray-900">Upload Media</h3>
            <Button variant="ghost" size="icon" onClick={resetForm} className="rounded-full">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Media title"
                  required
                  className="h-12 rounded-xl"
                  data-testid="media-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Type</Label>
                <Select value={formData.media_type} onValueChange={(v) => setFormData({ ...formData, media_type: v })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="media-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                className="rounded-xl resize-none"
                data-testid="media-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Committee</Label>
              <Select value={formData.committee} onValueChange={(v) => setFormData({ ...formData, committee: v })}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="media-committee-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mosque-dawah">Mosque and Da'wah</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Media gallery is only available for Mosque & Da'wah</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">File</Label>
              <Input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                accept={
                  formData.media_type === 'image' ? 'image/*' :
                  formData.media_type === 'video' ? 'video/*' :
                  formData.media_type === 'audio' ? 'audio/*' :
                  '.pdf,.doc,.docx'
                }
                required
                className="h-12 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                data-testid="media-file-input"
              />
              <p className="text-xs text-gray-500">Max file size: 50MB</p>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6" disabled={uploading} data-testid="media-submit-btn">
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl px-6">
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="aspect-video animate-pulse bg-gray-200 rounded-2xl"></Card>
          ))}
        </div>
      ) : media.length === 0 ? (
        <Card className="p-16 text-center rounded-3xl border-dashed">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <Image className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Media Yet</h3>
          <p className="text-gray-500">Upload your first media file to get started!</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {media.map((item) => (
            <Card key={item.media_id} className="overflow-hidden rounded-2xl group" data-testid={`admin-media-${item.media_id}`}>
              <div className="aspect-video bg-gray-100 flex items-center justify-center relative overflow-hidden">
                {item.media_type === "image" && (
                  <img src={item.file_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                )}
                {item.media_type === "video" && (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <Image className="w-8 h-8 text-white" />
                  </div>
                )}
                {item.media_type === "audio" && (
                  <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Image className="w-8 h-8 text-white" />
                  </div>
                )}
                {item.media_type === "document" && (
                  <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(item.media_id)} className="rounded-full bg-white/20 text-white hover:bg-white/30">
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <h4 className="font-medium text-sm text-gray-900 line-clamp-1">{item.title}</h4>
                <p className="text-xs text-gray-500 mt-1">{formatFileSize(item.file_size)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Events Tab
const EventsTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    committee: ""
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, date: new Date(formData.date).toISOString() };
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.event_id}`, data, { withCredentials: true });
        toast.success("Event updated!");
      } else {
        await axios.post(`${API}/events`, data, { withCredentials: true });
        toast.success("Event created!");
      }
      fetchEvents();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success("Event deleted!");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: new Date(event.date).toISOString().slice(0, 16),
      location: event.location,
      committee: event.committee || ""
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({ title: "", description: "", date: "", location: "", committee: "" });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Manage Events</h2>
          <p className="text-gray-600">Create and manage upcoming events and activities.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="create-event-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 rounded-3xl shadow-lg border-0">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl font-semibold text-gray-900">
              {editingEvent ? "Edit Event" : "Create New Event"}
            </h3>
            <Button variant="ghost" size="icon" onClick={resetForm} className="rounded-full">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Event title"
                  required
                  className="h-12 rounded-xl"
                  data-testid="event-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="h-12 rounded-xl"
                  data-testid="event-date-input"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Event location"
                  required
                  className="h-12 rounded-xl"
                  data-testid="event-location-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Committee (Optional)</Label>
                <Select value={formData.committee} onValueChange={(v) => setFormData({ ...formData, committee: v })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="event-committee-select">
                    <SelectValue placeholder="Select committee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mosque-dawah">Mosque and Da'wah</SelectItem>
                    <SelectItem value="project-maintenance">Project and Maintenance</SelectItem>
                    <SelectItem value="welfare-publicity">Welfare and Publicity</SelectItem>
                    <SelectItem value="finance-investment">Finance and Investment</SelectItem>
                    <SelectItem value="security-ethic">Security and Ethics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Event description..."
                rows={4}
                required
                className="rounded-xl resize-none"
                data-testid="event-description-input"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6" data-testid="event-submit-btn">
                {editingEvent ? "Update Event" : "Create Event"}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl px-6">
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 rounded-2xl animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </Card>
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card className="p-16 text-center rounded-3xl border-dashed">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Events Yet</h3>
          <p className="text-gray-500">Create your first event to get started!</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event.event_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`admin-event-${event.event_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-xl font-bold text-white leading-none">{format(new Date(event.date), "d")}</span>
                    <span className="text-xs text-primary-100 uppercase">{format(new Date(event.date), "MMM")}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{event.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{event.description}</p>
                    <p className="text-xs text-gray-400 mt-2">{event.location}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(event)} className="rounded-xl hover:bg-primary-50">
                    <Edit className="w-4 h-4 text-primary-900" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(event.event_id)} className="rounded-xl hover:bg-red-50">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Messages Tab
const MessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-gray-900">Contact Messages</h2>
        <p className="text-gray-600">View and manage messages from visitors and community members.</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 rounded-2xl animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </Card>
          ))}
        </div>
      ) : messages.length === 0 ? (
        <Card className="p-16 text-center rounded-3xl border-dashed">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Messages Yet</h3>
          <p className="text-gray-500">Messages from the contact form will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <Card key={message.message_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`message-${message.message_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                  <p className="text-sm text-gray-600">{message.name} &lt;{message.email}&gt;</p>
                  {message.phone && <p className="text-xs text-gray-500 mt-1">{message.phone}</p>}
                </div>
                <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                  {format(new Date(message.created_at), "MMM d, yyyy h:mm a")}
                </span>
              </div>
              <div className="bg-stone-50 p-4 rounded-xl">
                <p className="text-gray-700 text-sm leading-relaxed">{message.message}</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Users Tab
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.password) {
      toast.error("All fields are required");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users`, formData, { withCredentials: true });
      toast.success("Admin user created successfully!");
      fetchUsers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin user");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromote = async (userId, userName) => {
    if (!window.confirm(`Promote ${userName} to admin?`)) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/promote`, {}, { withCredentials: true });
      toast.success(`${userName} is now an admin!`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote user");
    }
  };

  const handleDemote = async (userId, userName) => {
    if (!window.confirm(`Remove admin privileges from ${userName}?`)) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/demote`, {}, { withCredentials: true });
      toast.success(`${userName} is no longer an admin`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to demote user");
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`${userName} has been deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setFormData({ name: "", email: "", password: "" });
  };

  const adminCount = users.filter(u => u.is_admin).length;
  const memberCount = users.filter(u => !u.is_admin).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600">Manage administrators and users.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="create-admin-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Create Admin
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-900">{adminCount}</p>
              <p className="text-sm text-amber-700">Administrators</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-900">{memberCount}</p>
              <p className="text-sm text-emerald-700">Members</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Create Admin Form */}
      {showForm && (
        <Card className="p-6 rounded-3xl shadow-lg border-0">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl font-semibold text-gray-900">Create New Admin</h3>
            <Button variant="ghost" size="icon" onClick={resetForm} className="rounded-full">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <form onSubmit={handleCreateAdmin} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Admin name"
                  required
                  className="h-12 rounded-xl"
                  data-testid="admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@example.com"
                  required
                  className="h-12 rounded-xl"
                  data-testid="admin-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 characters"
                required
                className="h-12 rounded-xl"
                data-testid="admin-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6" disabled={submitting} data-testid="admin-submit-btn">
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Admin
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl px-6">
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Users List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 rounded-2xl animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </Card>
          ))}
        </div>
      ) : users.length === 0 ? (
        <Card className="p-16 text-center rounded-3xl border-dashed">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Users Yet</h3>
          <p className="text-gray-500">Users will appear here once they register.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Admins Section */}
          <h3 className="font-heading text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            Administrators ({adminCount})
          </h3>
          {users.filter(u => u.is_admin).map((user) => (
            <Card key={user.user_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow border-l-4 border-amber-500" data-testid={`user-${user.user_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{user.name}</h4>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Joined {format(new Date(user.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDemote(user.user_id, user.name)} 
                    className="rounded-xl hover:bg-amber-50 text-amber-700"
                    data-testid={`demote-${user.user_id}`}
                  >
                    <ShieldOff className="w-4 h-4 mr-1" />
                    Demote
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDelete(user.user_id, user.name)} 
                    className="rounded-xl hover:bg-red-50 text-red-600"
                    data-testid={`delete-${user.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}

          {/* Members Section */}
          {memberCount > 0 && (
            <>
              <h3 className="font-heading text-lg font-semibold text-gray-900 flex items-center gap-2 mt-8">
                <Users className="w-5 h-5 text-emerald-600" />
                Members ({memberCount})
              </h3>
              {users.filter(u => !u.is_admin).map((user) => (
                <Card key={user.user_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`user-${user.user_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Users className="w-6 h-6 text-emerald-700" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{user.name}</h4>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Joined {format(new Date(user.created_at), "MMM d, yyyy")}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handlePromote(user.user_id, user.name)} 
                        className="rounded-xl hover:bg-emerald-50 text-emerald-700"
                        data-testid={`promote-${user.user_id}`}
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Make Admin
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(user.user_id, user.name)} 
                        className="rounded-xl hover:bg-red-50 text-red-600"
                        data-testid={`delete-${user.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
